import { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import ProductSearchInput, { SelectedVariant } from './ProductSearchInput';
import { DraftOrderResult } from '../services/customerService';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores';

interface LineItem {
  selectedVariant: SelectedVariant | null;
  quantity: number;
}

interface CreateOrderModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { lineItems: { variant_id: string; quantity: number }[]; note?: string }) => Promise<DraftOrderResult>;
  isSubmitting: boolean;
}

export default function CreateOrderModal({ open, onClose, onSubmit, isSubmitting }: CreateOrderModalProps) {
  const currency = useAuthStore((s) => s.currency);
  const [lineItems, setLineItems] = useState<LineItem[]>([{ selectedVariant: null, quantity: 1 }]);
  const [note, setNote] = useState('');
  const [draftResult, setDraftResult] = useState<DraftOrderResult | null>(null);
  const [copied, setCopied] = useState(false);

  const addLineItem = () => {
    setLineItems((prev) => [...prev, { selectedVariant: null, quantity: 1 }]);
  };

  const removeLineItem = (index: number) => {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const selectVariant = (index: number, variant: SelectedVariant) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, selectedVariant: variant } : item))
    );
  };

  const clearVariant = (index: number) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, selectedVariant: null } : item))
    );
  };

  const updateQuantity = (index: number, quantity: number) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, quantity } : item))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validItems = lineItems.filter((li) => li.selectedVariant !== null);
    if (validItems.length === 0) return;

    try {
      const result = await onSubmit({
        lineItems: validItems.map((li) => ({
          variant_id: li.selectedVariant!.variantId,
          quantity: li.quantity,
        })),
        note: note.trim() || undefined,
      });
      setDraftResult(result);
    } catch {
      // error handled by mutation's onError
    }
  };

  const handleClose = () => {
    setLineItems([{ selectedVariant: null, quantity: 1 }]);
    setNote('');
    setDraftResult(null);
    setCopied(false);
    onClose();
  };

  const copyInvoiceUrl = async () => {
    if (!draftResult?.invoice_url) return;
    try {
      await navigator.clipboard.writeText(draftResult.invoice_url);
      setCopied(true);
      toast.success('Payment link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-panel bg-surface border border-line p-6 shadow-l">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-lg font-semibold text-ink">
                    {draftResult ? 'Order Created' : 'Create Order'}
                  </Dialog.Title>
                  <button onClick={handleClose} className="rounded-xl p-1 text-ink-2 hover:bg-black/[0.04]">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {draftResult ? (
                  <div className="space-y-4">
                    <div className="rounded-card border border-success/20 bg-success/10 p-4">
                      <p className="text-sm font-medium text-success">
                        Draft order {draftResult.name} created
                      </p>
                      <p className="mt-1 text-sm text-success">
                        Total: {formatCurrency(Number(draftResult.total), draftResult.currency || currency)}
                      </p>
                      <p className="mt-1 text-xs text-success">
                        Status: {draftResult.status} — awaiting payment
                      </p>
                    </div>

                    {draftResult.invoice_url && (
                      <div>
                        <label className="mb-1 block text-sm font-medium text-ink-2">
                          Payment Link
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            readOnly
                            value={draftResult.invoice_url}
                            className="flex-1 rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink-2"
                          />
                          <button
                            type="button"
                            onClick={copyInvoiceUrl}
                            className="flex items-center gap-1 rounded-pill border border-line px-3 py-2 text-sm font-medium text-ink-2 hover:bg-black/[0.04]"
                          >
                            {copied ? (
                              <CheckIcon className="h-4 w-4 text-success" />
                            ) : (
                              <ClipboardDocumentIcon className="h-4 w-4" />
                            )}
                            {copied ? 'Copied' : 'Copy'}
                          </button>
                        </div>
                        <p className="mt-1.5 text-xs text-ink-3">
                          Send this link to the customer to complete payment.
                        </p>
                      </div>
                    )}

                    <div className="flex justify-end pt-2">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-ink"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-ink-2">
                        Line Items
                      </label>
                      <div className="space-y-2">
                        {lineItems.map((item, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <div className="flex-1">
                              <ProductSearchInput
                                onSelect={(variant) => selectVariant(index, variant)}
                                selectedVariant={item.selectedVariant}
                                onClear={() => clearVariant(index)}
                              />
                            </div>
                            <input
                              type="number"
                              min={1}
                              value={item.quantity}
                              onChange={(e) => updateQuantity(index, parseInt(e.target.value) || 1)}
                              className="w-20 rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
                            />
                            {lineItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeLineItem(index)}
                                className="rounded-xl p-2 text-ink-2 hover:bg-danger/10 hover:text-danger"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={addLineItem}
                        className="mt-2 flex items-center gap-1 text-sm text-brand hover:text-brand-ink"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Add item
                      </button>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-ink-2">
                        Note (optional)
                      </label>
                      <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
                        placeholder="Order note..."
                      />
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="rounded-pill border border-line px-4 py-2 text-sm font-medium text-ink-2 hover:bg-black/[0.04]"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting || lineItems.every((li) => li.selectedVariant === null)}
                        className="rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-ink disabled:opacity-50"
                      >
                        {isSubmitting ? 'Creating...' : 'Create Order'}
                      </button>
                    </div>
                  </form>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
